import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Drawer, Space, Button, Popconfirm, Spin, Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import * as RefundApi from '@/api/bill/refund';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import RefundByPaymentTypeModal, { IRefundRef } from '@/components/RefundByPaymentTypeModal';
import { parseFen2Currency } from '@/utils/number';
import { setSettlementAction } from '@/redux/actions/bill';
import { initRefundInfo, updateRefundInfo } from '@/redux/actions/bill/refund';
import { setOrderState } from '@/redux/actions/bill/settle';

import { MT_ZL, XC_ZL, DY_ZL } from '../config';
import RoomRate from './roomRate';
import Deposit from './deposit';
// import RefundStats from './stats';

function Refund(props) {
  const {
    refundAmounts,
    settlementNo,
    settlementAction,
    refundDrawerVisible,
    refundInfo,
    resvNo,
  } = props;

  const refundRef = useRef<IRefundRef>();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [undistributedRoomRate, setUndistributedRoomRate] = useState(0);
  const [undistributedDeposit, setUndistributedDeposit] = useState(0);

  const generateRefundParams = (info) => {
    const { roomRate, deposit } = info;
    const params = {
      settlementNo,
      roomRate: [],
      deposit: [],
    };

    if (Array.isArray(roomRate)) {
      params.roomRate = roomRate
        .filter((val) => val.defaultRefundAmount !== 0)
        .map((item) => {
          return {
            no: item.no,
            paymentNo: item.paymentNo,
            refundAmount: item.defaultRefundAmount,
          };
        });
    }

    if (Array.isArray(deposit)) {
      params.deposit = deposit
        .filter((val) => val.defaultRefundAmount !== 0)
        .map((item) => {
          return {
            no: item.no,
            paymentNo: item.paymentNo,
            refundAmount: item.defaultRefundAmount,
          };
        });
    }
    return params;
  };

  const closeDrawer = () => {
    // parent component callback
    if (props.refundCallback && typeof props.refundCallback === 'function')
      props.refundCallback(true);
  };

  const columns: ColumnsType<any> = [
    {
      title: intl.get('bill_refound_res_pay_channel'),
      dataIndex: 'paymentType',
      width: 180,
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_refound_res_detail'),
      dataIndex: 'detail',
      render: (_, record) => {
        return `${record.paymentType}-${intl.get('bill_refound_res_channel_no')}${
          record.paymentNo
        }`;
      },
    },
    {
      title: intl.get('bill_refound_res_pay'),
      dataIndex: 'payAmount',
      width: 120,
      align: 'right',
      render: (val) => parseFen2Currency(val),
    },
    {
      title: intl.get('bill_refound_res_pay_time'),
      dataIndex: 'paymentTime',
      width: 200,
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT),
    },
    {
      title: intl.get('bill_refound_res_refound_amount'),
      dataIndex: 'refundAmount',
      width: 120,
      align: 'right',
      render: (val) => parseFen2Currency(val),
    },
    {
      title: intl.get('bill_refound_res_title'),
      dataIndex: 'status',
      width: 150,
      render: (v, r) => {
        if (v === 1) {
          return <span>{intl.get('bill_refound_res_success')}</span>;
        }
        if (v === 3) {
          return <span style={{ color: '#ff4d4f' }}>{r.reason}</span>;
        }

        return '';
      },
    },
  ];

  const openResultModal = (result) => {
    const resultList = result;

    Modal.info({
      title: (
        <span style={{ fontFamily: 'Alibaba-PuHuiTi-M' }}>
          {intl.get('bill_refound_res_title')}
        </span>
      ),
      width: 1200,
      icon: null,
      content: (
        <>
          <p style={{ margin: '24px 0' }}>{intl.get('bill_refound_res_refund_fail_tips')}</p>
          <Table rowKey="paymentNo" columns={columns} dataSource={resultList} pagination={false} />
        </>
      ),
      okText: intl.get('app_confirm_confirm_btn'),
      onOk: () => {
        closeDrawer();

        // reset order state, for settle
        props.setOrderState(0);

        // parent component callback
        if (props.refundCallback && typeof props.refundCallback === 'function')
          props.refundCallback(false);
      },
    });
  };

  // 确认退款
  const confirmRefund = async () => {
    try {
      setSubmitLoading(true);

      const params = generateRefundParams(refundInfo);
      let res;
      // v2.9.1 日志
      if (resvNo) {
        // 部分结账退款
        res = await RefundApi.partialSettleRefund({ ...params, resvNo });
      } else {
        res = await RefundApi.confirmRefund(params);
      }

      setSubmitLoading(false);
      openResultModal(res?.data);
    } catch (err) {
      setSubmitLoading(false);
      openResultModal([]);
    }
  };

  const submit = async () => {
    const { roomRate, deposit } = refundInfo;
    const tmpArr = Array.prototype.concat.call(roomRate || [], deposit || []);

    const dataSource = tmpArr
      .filter((val) => val.defaultRefundAmount !== 0)
      .map((item) => {
        return {
          id: item.no,
          subjectName: item.subjectName,
          paymentType: item.paymentType,
          refundAmount: item.defaultRefundAmount,
        };
      });

    // 退款二次确认
    refundRef.current?.showModal({
      dataSource,
      onConfirm: async () => {
        await confirmRefund();
      },
    });
  };

  const initInfo = () => {
    if (settlementNo && settlementAction === 'refund') {
      props.initRefundInfo({
        settlementNo,
      });
    }
  };

  const changeVisible = (visible) => {
    if (visible) {
      initInfo();
    } else {
      // reset states
      setUndistributedRoomRate(0);
      setUndistributedDeposit(0);

      // reset settlementNo & settlementAction
      props.setSettlementAction('');

      // clear refundInfo cache
      props.updateRefundInfo({
        refundInfo: {
          roomRate: [],
          deposit: [],
        },
      });
    }
  };

  const drawerFooter = () => {
    // 美团直连、携程直连、抖音直连预付款退款二次确认
    const refundConfirm =
      refundInfo.roomRate &&
      refundInfo.roomRate.length > 0 &&
      refundInfo.roomRate.some(
        (item) =>
          item.paymentType === MT_ZL || item.paymentType === XC_ZL || item.paymentType === DY_ZL,
      );

    const hasMT =
      refundInfo.roomRate &&
      refundInfo.roomRate.length > 0 &&
      refundInfo.roomRate.some((item) => item.paymentType === MT_ZL);

    const hasXC =
      refundInfo.roomRate &&
      refundInfo.roomRate.length > 0 &&
      refundInfo.roomRate.some((item) => item.paymentType === XC_ZL);

    const hasDY =
      refundInfo.roomRate &&
      refundInfo.roomRate.length > 0 &&
      refundInfo.roomRate.some((item) => item.paymentType === DY_ZL);

    let channelName = hasMT ? intl.get('app_meituan') : '';
    channelName = hasXC ? intl.get('app_trip') : '';
    channelName = hasDY ? intl.get('app_tiktok') : '';

    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        {refundConfirm ? (
          <Popconfirm
            title={
              <div>
                <span>{intl.get('bill_cross_refound_confirm_refound_title', { channelName })}</span>
                <p style={{ color: 'red' }}>{intl.get('bill_cross_refound_alert')}</p>
              </div>
            }
            okText={intl.get('bill_cross_refound_confirm')}
            cancelText={intl.get('bill_cross_refound_click_wrong')}
            okButtonProps={{
              disabled: undistributedRoomRate !== 0 || undistributedDeposit !== 0,
              loading: submitLoading,
            }}
            onConfirm={() => submit()}
          >
            <Button
              type="primary"
              danger
              disabled={undistributedRoomRate !== 0 || undistributedDeposit !== 0}
            >
              {intl.get('bill_config_refound')}
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="primary"
            danger
            disabled={undistributedRoomRate !== 0 || undistributedDeposit !== 0}
            loading={submitLoading}
            onClick={() => submit()}
          >
            {intl.get('bill_config_refound')}
          </Button>
        )}
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  return (
    <Drawer
      width="83.33%"
      open={refundDrawerVisible}
      title={intl.get('bill_config_refound')}
      maskClosable={false}
      closable={false}
      destroyOnClose
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <RefundByPaymentTypeModal ref={refundRef} />
      <Spin spinning={submitLoading}>
        <RoomRate
          refundAmounts={refundAmounts}
          setRestRoomRate={(restAmount) => setUndistributedRoomRate(restAmount)}
        />
        <Deposit
          refundAmounts={refundAmounts}
          setRestDeposit={(restAmount) => setUndistributedDeposit(restAmount)}
        />
      </Spin>
      {/* <RefundStats /> */}
    </Drawer>
  );
}

const mapStateToProps = (state) => {
  const { refundInfo } = state.refund;
  return { refundInfo };
};

export default connect(mapStateToProps, {
  setSettlementAction,
  initRefundInfo,
  updateRefundInfo,
  setOrderState,
})(Refund);
